import Auth from '@aws-amplify/auth'
import Amplify from '@aws-amplify/core'
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { useEffect, useState } from 'react'
import { Switch, Route } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { useActionRunner } from 'src/modules/notification'
import { Container, Typography } from 'src/modules/ui'

import { useAuth } from '../hooks'
import { checkUserAuthState } from '../utils'
import awsConfig from '../awsConfig'
import ForgotPassword from './ForgotPassword'
import Login from './Login'
import ResetPassword from './ResetPassword'
import SignUp from './SignUp'
import VerifyEmail from './VerifyEmail'

Amplify.configure(awsConfig)

const AuthComponent = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [authState, setAuthState] = useState()
  const {
    fetchUser,
    user,
    signOut,
    // getFeatureSwitches,
    // featureSwitches,
  } = useAuth()
  const [cognitoUser, setCognitoUser] = useState()

  const [doFetchUser] = useActionRunner(fetchUser, {
    errorNotification: result => {
      try {
        unwrapResult(result)
      } catch ({ status }) {
        if (status === 401) {
          return 'Your user has been deleted'
        }
      }
      return 'Oops there was an error logging in'
    },
  })

  // const [doGetFeatureSwitches] = useActionRunner(getFeatureSwitches)

  useEffect(() => {
    onAuthUIStateChange(handleStateChange)
  }, [])

  // useEffect(() => {
  //   doGetFeatureSwitches()
  // }, [])

  // useEffect(() => {
  //   doGetFeatureSwitches()
  // }, [])

  useEffect(() => {
    const checkAuthenticatedUser = async () => {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser()
        if (cognitoUser) {
          await checkUserAuthState(cognitoUser, handleStateChange)
        }
      } catch (err) {
      } finally {
        setLoading(false)
      }
    }
    checkAuthenticatedUser()
  }, [])

  useEffect(() => {
    const doFetch = async () => {
      const result = await doFetchUser()
      try {
        const user = unwrapResult(result)
        Sentry.setUser({ email: user.email, id: user.id })
      } catch ({ status }) {
        if (status === 401) {
          setTimeout(signOut, 5000)
        }
      }
    }
    const loggedIn = Boolean(authState === AuthState.SignedIn && cognitoUser)
    if (loggedIn) {
      doFetch()
    }
  }, [authState, cognitoUser])

  const handleStateChange = (nextAuthState, authData) => {
    setAuthState(nextAuthState)
    setCognitoUser(authData)
  }

  const handleClickForgottenPassword = () =>
    handleStateChange(AuthState.ForgotPassword, {})

  const handleClickBackToSignin = () => handleStateChange(AuthState.SignIn, {})

  return loading ? (
    <Container>
      <Typography>Loading...</Typography>
    </Container>
  ) : authState === AuthState.SignedIn && cognitoUser && user ? (
    children
  ) : (
    <Container maxWidth="sm">
      <Switch>
        <Route path="/signup">
          <SignUp onStateChange={handleStateChange} />
        </Route>
        <Route>
          {[AuthState.SignIn, AuthState.SignedOut, undefined].includes(
            authState
          ) ? (
            <Login
              onClickForgottenPassword={handleClickForgottenPassword}
              onStateChange={handleStateChange}
            />
          ) : authState === AuthState.ForgotPassword ? (
            <ForgotPassword
              onClickBackToSignin={handleClickBackToSignin}
              onStateChange={handleStateChange}
              user={cognitoUser}
            />
          ) : authState === AuthState.VerifyContact ? (
            <VerifyEmail
              onClickBackToSignin={handleClickBackToSignin}
              onStateChange={handleStateChange}
              user={cognitoUser}
            />
          ) : authState === AuthState.ResetPassword ? (
            <ResetPassword
              onClickBackToSignin={handleClickBackToSignin}
              onStateChange={handleStateChange}
              user={cognitoUser}
            />
          ) : null}
        </Route>
      </Switch>
    </Container>
  )
}

export default AuthComponent
